import { useQuery } from "@tanstack/react-query";
import { competencyById, type Vertical } from "@talenthop/shared";
import { api } from "../../lib/api.js";

/**
 * The active assessment verticals (GET /verticals) — the recruiter-facing read of the vocabulary
 * an owner maintains in the admin console. Every screen that renders a competencyId needs this:
 * the wire carries snake_case ids, and a human must never be shown `pastoral_care`.
 *
 * Cached for the session (verticals change about as often as the price book) and shared by key
 * across the users persona, so a Requisitions list and a New Request wizard cost one request.
 */
interface VerticalsResponse {
  verticals: Vertical[];
}

export interface VerticalsResult {
  verticals: Vertical[];
  /** Resolve a key to its vertical; undefined when the key is unknown or deactivated since. */
  byKey: (key: string | null | undefined) => Vertical | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useVerticals(): VerticalsResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["verticals"],
    queryFn: () => api.get<VerticalsResponse>("/verticals"),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const verticals = data?.verticals ?? [];
  return {
    verticals,
    byKey: (key) => (key ? verticals.find((v) => v.key === key) : undefined),
    isLoading,
    isError,
  };
}

/**
 * Turn a snake_case id into something readable. Used ONLY when the vertical can't be resolved —
 * a requisition pinned to a vertical the owner has since deactivated still has to render its
 * report, and "Pastoral care" beats a raw id even if we can't reach the real label.
 */
export function prettyId(id: string): string {
  const spaced = id.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A competency's human label, falling back to a prettified id when the vertical is unresolved. */
export function competencyLabel(
  vertical: Pick<Vertical, "competencies"> | undefined,
  competencyId: string,
): string {
  return (vertical && competencyById(vertical, competencyId)?.label) || prettyId(competencyId);
}

/**
 * The "what good looks like" half of a rubric anchor, for use as a one-line picker hint.
 * Anchors are stored in the house format "0-40: …; 41-70: …; 71-100: …" — the top band is the
 * only part a recruiter choosing what to assess actually needs. Anything that doesn't match the
 * format degrades to the whole string rather than showing nothing.
 */
export function anchorHint(anchors: string): string {
  const top = anchors.split(";").find((part) => /^\s*7\d\s*-\s*100\s*:/.test(part));
  const text = (top ?? anchors).replace(/^\s*\d+\s*-\s*\d+\s*:\s*/, "").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
